// reducers.js
import {
  SET_WALLPAPER_TRANSPARENCY,
  SET_WALLPAPER_RESIZING,
  SET_WALLPAPER_OVERLAY_COLOR,
  SET_BG_WALLPAPER,
  SET_IS_PATTERN,
  SET_SEGMENT_BG_COLOR,
  SET_CURRENT_BG,
  SET_SELECTED_COLOR_TYPE,
  SET_PATTERN_COLOR,
  SET_BG_GRADIENT,
  UPDATE_WALLPAPER_INITAL_STATE,
  RESET_WHEEL_BG_TO_INITIAL_STATE,
  SET_WALLPAPER_HUE_PERCENTAGE,
  SET_WALLPAPER_HUE_COLOR,
} from '../types';

const initialState = {
  wallpaperTransparency: 100,
  wallpaperResizing: 20,
  wallpaperOverlayColor: '#ffffff',
  bgWallpaper: 'linear-gradient(45deg, #F7FED5, #CCFEFF, #FDCFFE, #F5EEDE)',
  isPattern: false,
  segmentBgColor: '#ffffff',
  currentBg: 3,
  selectedColorType: 1,
  patternColor: '#000000',
  wallpaperHuePercentage: 0,
  wallpaperHueColor: '#ffffff',
};

/**
 * Shallow copy of `payload` with `undefined`-valued keys removed, so spreading it
 * over `initialState` cannot re-introduce `undefined` where a default exists.
 * A non-object payload (null / undefined) contributes nothing.
 */
const omitUndefined = (payload) => {
  if (!payload || typeof payload !== 'object') return {};
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );
};

const wheelWallpaperReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_WALLPAPER_TRANSPARENCY:
      return { ...state, wallpaperTransparency: action.payload };
    case SET_WALLPAPER_RESIZING:
      return { ...state, wallpaperResizing: action.payload };
    case SET_WALLPAPER_OVERLAY_COLOR:
      return { ...state, wallpaperOverlayColor: action.payload, currentBg: 1 };
    case SET_BG_WALLPAPER:
      return {
        ...state,
        bgWallpaper: action.payload.value,
        isPattern: action.payload.isPattern,
        currentBg: action.payload.isPattern ? 1 : 2,
      };
    case SET_BG_GRADIENT:
      return {
        ...state,
        bgWallpaper: action.payload.value,
        currentBg: 3,
      };
    case SET_IS_PATTERN:
      return { ...state, isPattern: action.payload };
    case SET_SEGMENT_BG_COLOR:
      return { ...state, segmentBgColor: action.payload, currentBg: 0 };
    case SET_CURRENT_BG:
      return { ...state, currentBg: action.payload };
    case SET_SELECTED_COLOR_TYPE:
      return { ...state, selectedColorType: action.payload, currentBg: 1 };
    case SET_PATTERN_COLOR:
      return { ...state, patternColor: action.payload, currentBg: 1 };
    case UPDATE_WALLPAPER_INITAL_STATE:
      // NORMALIZE AT THE BOUNDARY (do not replace the slice wholesale).
      //
      // This action hydrates the wheel-level wallpaper slice from the API's
      // `wallpaper_settings`. That column is NULLABLE — a wheel that was never
      // themed comes back with `wallpaper_settings: null`, and the dispatch site
      // (pages/SegmentBuilder) sends `convertSnakeToCamel(response.wallpaper_settings || {})`,
      // i.e. `{}`. The old `return action.payload` REPLACED the slice with that
      // empty object, destroying every default in `initialState` — `patternColor`,
      // `currentBg`, `bgWallpaper`, `wallpaperOverlayColor`, `wallpaperTransparency`
      // all became `undefined` for the whole session.
      //
      // That is a CRASH, not a cosmetic default loss: Shared/Wallpaper's
      // pattern-colour seeding effect fires only when `patternColor === undefined`
      // and then calls `handlePatternColorInitialChange`, an OPTIONAL callback the
      // wheel-level caller (Main/WallpaperTab) does not pass -> TypeError -> the
      // Design > Wallpaper pane unmounts. Guarding only the crash site would leave
      // every other consumer of this slice silently rendering with undefined
      // colours, so the fix belongs here, at the source of the null (G-118).
      //
      // Merging over `initialState` and dropping undefined-valued keys means a
      // null / empty / SPARSE payload keeps the documented defaults, while a full
      // payload (a themed wheel) still wins on every key it actually carries —
      // byte-identical behaviour for wheels that have settings.
      return {
        ...initialState,
        ...omitUndefined(action.payload),
      };
    case RESET_WHEEL_BG_TO_INITIAL_STATE:
      return { ...initialState };
    case SET_WALLPAPER_HUE_PERCENTAGE:
      return { ...state, wallpaperHuePercentage: action.payload };
    case SET_WALLPAPER_HUE_COLOR:
      return { ...state, wallpaperHueColor: action.payload };
    default:
      return state;
  }
};

export default wheelWallpaperReducer;
